/**
 * Перевод между тем, как человек видит дату, и тем, как её хранит сервер.
 *
 * Поля ввода даты и времени на странице работают в поясе устройства,
 * а на сервере всё лежит в UTC. Переводить это в каждом месте по-своему —
 * верный способ однажды сдвинуть событие на час и долго не понимать,
 * почему напоминание приходит не вовремя.
 */

const pad = (n: number): string => String(n).padStart(2, '0')

/** Дата для <input type="date"> — по местному времени устройства. */
export const dateValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Время для <input type="time">. */
export const timeValue = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`

/** Обратно: то, что человек набрал в двух полях, — в момент времени. */
export function fromInputs(date: string, time: string | null): Date {
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = (time ?? '00:00').split(':').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0)
}

const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

const MONTHS_NOMINATIVE = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']

export const monthTitle = (date: Date): string =>
  `${MONTHS_NOMINATIVE[date.getMonth()]} ${date.getFullYear()}`

/**
 * «сентября», а не «сентябрь».
 *
 * Стандартное форматирование даёт именительный падеж, и получается
 * «14 сентябрь». Своя таблица короче, чем объяснение, почему так вышло.
 */
export const monthOf = (date: Date): string => MONTHS[date.getMonth()] ?? ''

/** «3 сентября, четверг» — так день читается сразу, без счёта в уме. */
export function dayTitle(date: Date): string {
  const today = new Date()
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()

  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const isTomorrow =
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate()

  const base = `${date.getDate()} ${MONTHS[date.getMonth()]}, ${WEEKDAYS[date.getDay()]}`

  if (sameDay) return `Сегодня · ${base}`
  if (isTomorrow) return `Завтра · ${base}`
  return base
}

export const clockTime = (iso: string): string => {
  const date = new Date(iso)
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Ключ дня для группировки: по местному времени, а не по UTC. */
export const dayKey = (iso: string | Date): string =>
  dateValue(typeof iso === 'string' ? new Date(iso) : iso)

export const sameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

/** Понедельник недели, в которую попадает день: неделя начинается с него. */
export function mondayOf(date: Date): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7))
  return copy
}

/** Сетка месяца: полные недели, включая хвосты соседних месяцев. */
export function monthGrid(month: Date): Date[] {
  const first = mondayOf(new Date(month.getFullYear(), month.getMonth(), 1))
  const days: Date[] = []

  for (let i = 0; i < 42; i += 1) {
    const day = new Date(first)
    day.setDate(first.getDate() + i)
    days.push(day)
    // Шести недель хватает на любой месяц, но лишнюю неделю не рисуем
    if (i >= 34 && day.getMonth() !== month.getMonth() && day.getDay() === 0) break
  }

  return days
}

export const KIND_ICONS: Record<string, string> = {
  EVENT: '📌',
  BIRTHDAY: '🎂',
  ACTIVITY: '🏅',
  APPOINTMENT: '🩺',
  OUTING: '🎫',
  DEADLINE: '⏳',
}

export const KIND_NAMES: Record<string, string> = {
  EVENT: 'Событие',
  BIRTHDAY: 'День рождения',
  ACTIVITY: 'Занятие',
  APPOINTMENT: 'Приём у врача',
  OUTING: 'Поездка, кино, концерт',
  DEADLINE: 'Срок',
}
